import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Beaker, X, Loader2, Search, Power } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface MP {
  id: number
  codigo: string
  nome: string
  categoria: string | null
  unidade: string
  custo_unitario: string
  estoque_atual: string
  estoque_minimo: string
  fornecedor_id: number | null
  fornecedor_nome: string | null
  origem: string
  situacao: string
  observacoes: string | null
}

const UNIDADES = ['KG', 'G', 'L', 'ML', 'UN', 'PC', 'M', 'M2', 'M3']

export default function IndMateriasPrimasPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<MP | null | 'new'>(null)
  const [busca, setBusca] = useState('')

  const { data = [], isLoading } = useQuery<MP[]>({
    queryKey: ['ind-mps', busca],
    queryFn: async () =>
      (await api.get('/industrial/materias-primas', { params: { q: busca || undefined } })).data,
  })

  const toggleSituacao = useMutation({
    mutationFn: async ({ id, situacao }: { id: number; situacao: string }) =>
      (await api.patch(`/industrial/materias-primas/${id}/situacao`, null, { params: { situacao } })).data,
    onSuccess: () => {
      toast.success('Atualizado')
      qc.invalidateQueries({ queryKey: ['ind-mps'] })
    },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Beaker className="text-brand-600" /> Matérias-primas
          </h1>
          <p className="text-sm text-ink-500">Cadastro de insumos, cargas e aditivos</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('new')}>
          <Plus size={14} /> Nova MP
        </button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            type="text"
            placeholder="Buscar por código ou nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(data, (m) => setModal(m))}
            className="input pl-9 w-full"
          />
        </div>
        <div className="text-xs text-ink-500 self-center">{data.length} registro(s)</div>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nome</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Unidade</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Custo R$</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Estoque</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Origem</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={8} className="text-center py-6 text-ink-400">Carregando…</td></tr>}
            {!isLoading && data.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-ink-500">Nenhuma MP cadastrada.</td></tr>
            )}
            {data.map((m) => {
              const semCusto = Number(m.custo_unitario) === 0
              const estoqueBaixo = Number(m.estoque_atual) < Number(m.estoque_minimo) && Number(m.estoque_minimo) > 0
              return (
                <tr key={m.id}
                  onClick={() => setModal(m)}
                  className={`hover:bg-violet-50 cursor-pointer ${m.situacao === 'INATIVO' ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs">{m.codigo}</td>
                  <td className="px-3 py-2 font-medium">{m.nome}</td>
                  <td className="px-3 py-2 text-center text-xs">{m.unidade}</td>
                  <td className={`px-3 py-2 text-right text-xs font-mono ${semCusto ? 'text-amber-600' : ''}`}>
                    {Number(m.custo_unitario).toFixed(4)}
                    {semCusto && <span className="ml-1" title="Sem custo">⚠</span>}
                  </td>
                  <td className={`px-3 py-2 text-right text-xs font-mono ${estoqueBaixo ? 'text-red-600' : ''}`}>
                    {Number(m.estoque_atual).toFixed(2)}
                    {estoqueBaixo && <span className="ml-1" title="Abaixo do mínimo">🔴</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${
                      m.origem === 'IMPORTADO' ? 'bg-blue-100 text-blue-700' : 'bg-ink-100 text-ink-700'
                    }`}>
                      {m.origem}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => toggleSituacao.mutate({ id: m.id, situacao: m.situacao === 'ATIVO' ? 'INATIVO' : 'ATIVO' })}
                      className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                        m.situacao === 'ATIVO' ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-200 text-ink-600'
                      }`}
                    >
                      {m.situacao}
                    </button>
                  </td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setModal(m)} className="text-ink-500 hover:text-brand-600 p-1">
                      <Edit size={14} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <MPModal
          mp={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['ind-mps'] }); setModal(null) }}
        />
      )}
    </div>
  )
}


function MPModal({ mp, onClose, onSuccess }: { mp: MP | null; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    codigo: mp?.codigo || '',
    nome: mp?.nome || '',
    categoria: mp?.categoria || '',
    unidade: mp?.unidade || 'KG',
    custo_unitario: Number(mp?.custo_unitario) || 0,
    estoque_atual: Number(mp?.estoque_atual) || 0,
    estoque_minimo: Number(mp?.estoque_minimo) || 0,
    fornecedor_nome: mp?.fornecedor_nome || '',
    observacoes: mp?.observacoes || '',
  })

  const mut = useMutation({
    mutationFn: async () => {
      if (mp) return (await api.put(`/industrial/materias-primas/${mp.id}`, form)).data
      return (await api.post('/industrial/materias-primas', form)).data
    },
    onSuccess: () => {
      toast.success(mp ? 'MP atualizada' : 'MP cadastrada')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const valido = form.codigo.trim() && form.nome.trim()

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Beaker size={18} /> {mp ? `Editar ${mp.codigo}` : 'Nova matéria-prima'}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Código *</label>
              <input className="input" value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: 0001 ou MP-001" />
            </div>
            <div>
              <label className="label">Unidade</label>
              <select className="input" value={form.unidade}
                onChange={(e) => setForm({ ...form, unidade: e.target.value })}>
                {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Nome *</label>
            <input className="input" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value.toUpperCase() })}
              placeholder="Ex: NEGRO DE FUMO" />
          </div>
          <div>
            <label className="label">Categoria</label>
            <input className="input" value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              placeholder="Ex: AGENTE ATIVADOR / CARGA / PIGMENTO" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Custo unitário (R$)</label>
              <input type="number" step="0.0001" className="input" value={form.custo_unitario}
                onChange={(e) => setForm({ ...form, custo_unitario: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Estoque atual</label>
              <input type="number" step="0.01" className="input" value={form.estoque_atual}
                onChange={(e) => setForm({ ...form, estoque_atual: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Estoque mínimo</label>
              <input type="number" step="0.01" className="input" value={form.estoque_minimo}
                onChange={(e) => setForm({ ...form, estoque_minimo: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <label className="label">Fornecedor (nome)</label>
            <input className="input" value={form.fornecedor_nome}
              onChange={(e) => setForm({ ...form, fornecedor_nome: e.target.value })} />
          </div>
          <div>
            <label className="label">Observações</label>
            <textarea className="input" rows={2} value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
